import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { request } from '@/lib/request'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CodeEditor } from '@/components/CodeEditor'
import { Plus, Trash2, Save } from 'lucide-react'
import { uniqueId } from 'lodash'

interface Param {
  key?: string
  param: string
  desc: string
  type: string
  default: string
  version: string
}

interface ParamsTableProps {
  value?: Param[]
  onChange?: (value: Param[]) => void
}

function ParamsTable({ value = [], onChange }: ParamsTableProps) {
  const [params, setParams] = useState<Param[]>(
    value.map((v) => ({ ...v, key: v.key || uniqueId() }))
  )

  // Sync external value changes only when loading data from server
  useEffect(() => {
    // Only sync if external value is provided and internal state is still empty
    // This handles the case when data is loaded from server after component mount
    if (value.length > 0 && params.length === 0) {
      setParams(value.map((v) => ({ ...v, key: v.key || uniqueId() })))
    }
  }, [value, params.length])

  const handleAdd = () => {
    const newParams = [
      ...params,
      {
        key: uniqueId(),
        param: '',
        desc: '',
        type: '',
        default: '',
        version: '',
      },
    ]
    setParams(newParams)
    onChange?.(newParams.map(({ key, ...rest }) => rest))
  }

  const handleDelete = (index: number) => {
    const newParams = params.filter((_, i) => i !== index)
    setParams(newParams)
    onChange?.(newParams.map(({ key, ...rest }) => rest))
  }

  const handleChange = (index: number, field: keyof Param, value: string) => {
    const newParams = [...params]
    newParams[index] = { ...newParams[index], [field]: value }
    setParams(newParams)
    onChange?.(newParams.map(({ key, ...rest }) => rest))
  }

  return (
    <div className="space-y-4">
      <Button type="button" size="sm" onClick={handleAdd}>
        <Plus className="mr-2 h-4 w-4" />
        添加参数
      </Button>

      {params.length > 0 && (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>参数</TableHead>
                <TableHead>说明</TableHead>
                <TableHead>类型</TableHead>
                <TableHead>默认值</TableHead>
                <TableHead>版本</TableHead>
                <TableHead className="w-20">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {params.map((param, index) => (
                <TableRow key={param.key}>
                  <TableCell>
                    <Input
                      value={param.param}
                      onChange={(e) => handleChange(index, 'param', e.target.value)}
                      placeholder="参数名"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={param.desc}
                      onChange={(e) => handleChange(index, 'desc', e.target.value)}
                      placeholder="说明"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={param.type}
                      onChange={(e) => handleChange(index, 'type', e.target.value)}
                      placeholder="类型"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={param.default}
                      onChange={(e) => handleChange(index, 'default', e.target.value)}
                      placeholder="默认值"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={param.version}
                      onChange={(e) => handleChange(index, 'version', e.target.value)}
                      placeholder="版本"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

export default function AddInterface() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    desc: '',
    path: '',
    require_login: true,
    version: '',
    params: [] as Param[],
    return_params: [] as Param[],
    code: '',
  })

  useEffect(() => {
    if (id) {
      loadData()
    }
  }, [id])

  const loadData = async () => {
    try {
      const resp = await request('/system/api/query/system_apis', {
        page_size: 1,
        page_num: 1,
        query_filter: { id },
      })
      if (resp && resp.code === 0 && resp.data.list.length) {
        setFormData(resp.data.list[0])
      }
    } catch (error) {
      alert('加载失败')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const data = id ? { ...formData, id } : formData
      const url = id
        ? '/system/api/update/system_apis'
        : '/system/api/add/system_apis'

      const resp = await request(url, data)
      if (resp && resp.code === 0) {
        alert('保存成功')
        navigate('/admin/interface', { replace: true })
      } else {
        alert('保存失败: ' + resp?.msg)
      }
    } catch (error) {
      alert('保存失败')
    }
  }

  const handleCancel = () => {
    navigate('/admin/interface', { replace: true })
  }

  return (
    <div className="h-full p-6 overflow-auto">
      <h1 className="text-2xl font-bold mb-6">{id ? '编辑' : '添加'}接口</h1>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-6xl" key={id || 'add'}>
        <div className="space-y-2">
          <Label htmlFor="desc">说明</Label>
          <Input
            id="desc"
            value={formData.desc}
            onChange={(e) => setFormData({ ...formData, desc: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="path">路径</Label>
          <Input
            id="path"
            value={formData.path}
            onChange={(e) => setFormData({ ...formData, path: e.target.value })}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="require_login"
            checked={formData.require_login}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, require_login: checked })
            }
          />
          <Label htmlFor="require_login">是否验证</Label>
        </div>

        <div className="space-y-2">
          <Label htmlFor="version">版本</Label>
          <Input
            id="version"
            value={formData.version}
            onChange={(e) => setFormData({ ...formData, version: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label>请求参数</Label>
          <ParamsTable
            value={formData.params}
            onChange={(value) => setFormData({ ...formData, params: value })}
          />
        </div>

        <div className="space-y-2">
          <Label>返回参数</Label>
          <ParamsTable
            value={formData.return_params}
            onChange={(value) => setFormData({ ...formData, return_params: value })}
          />
        </div>

        <div className="space-y-2">
          <Label>代码</Label>
          <CodeEditor
            value={formData.code}
            onChange={(value) => setFormData({ ...formData, code: value })}
            language="python"
            isTest
          />
        </div>

        <div className="flex gap-4">
          <Button type="submit">
            <Save className="mr-2 h-4 w-4" />
            提交
          </Button>
          <Button type="button" variant="outline" onClick={handleCancel}>
            取消
          </Button>
        </div>
      </form>
    </div>
  )
}
